import React, { Fragment, useState } from 'react';
import { Modal } from '@/Components/Modal/Modal';
import { Label } from '@/Components/Controls/Label/Label';
import { FormInput } from '@/Components/Controls/Input/FormInput';
import { FormData, useEmailPreviewModalForm } from './hooks/useEmailPreviewModalForm';
import { Button } from '@/Components/Controls/Button/Button';
import { BUTTON_THEMES } from '@/Components/Controls/Button/ButtonThemes';
import { DeleteIcon } from '@/Static';
import styles from './EmailPreviewModal.scss';
import { useLocalization } from '@/Hooks';
import { ModalDialog } from '@/Components/ModalDialog/ModalDialog';
import { ModalDataType } from '@/Types/ModalDataType';
import { useSelector } from 'react-redux';
import { emailSendModalSelectors } from '@/ModalWindows/EmailSendModal/services/selectors';
import { ButtonsWrapper } from '@/Components/Forms/ButtonsSection/ButtonsSection';

type Props = {
  onClose: () => void;
};

export const EmailPreviewModal = ({ onClose }: Props): React.ReactElement => {
  const {
    buttons,
    modalWindows: {
      emailSendModal: { EmailPreviewModal, DeleteAttachmentModal },
    },
  } = useLocalization();

  const data = useSelector(emailSendModalSelectors.emailPreviewModalData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<ModalDataType<number>>({ visible: false });

  const {
    onSubmit,
    rules: { required, valueMustBeEmail },
    arrays: { attachments },
    methods: { register, errors },
  } = useEmailPreviewModalForm(data);

  return (
    <Modal
      onClose={onClose}
      tourKey={'emailSendTour'}
      modalId={'emailPreviewModal'}
      description={{
        title: EmailPreviewModal.Title,
      }}
    >
      {showDeleteConfirm.visible && (
        <ModalDialog
          title={DeleteAttachmentModal.Title}
          text={DeleteAttachmentModal.Text}
          onOk={() => attachments.remove(showDeleteConfirm.data)}
          onClose={() => setShowDeleteConfirm({ visible: false })}
          okButtonText={buttons.Yes}
          cancelButtonText={buttons.No}
        />
      )}

      <form className={styles.emailPreviewModal} onSubmit={onSubmit}>
        <Label text={EmailPreviewModal.To} />
        <FormInput<FormData>
          name={'to'}
          register={register}
          rules={{ ...required, ...valueMustBeEmail }}
          error={errors}
        />

        <Label text={EmailPreviewModal.Subject} />
        <FormInput<FormData> name={'subject'} register={register} rules={required} error={errors} />

        <Label text={EmailPreviewModal.Cc} />
        <FormInput<FormData> name={'cc'} register={register} rules={valueMustBeEmail} error={errors} />

        <Label text={EmailPreviewModal.Bcc} />
        <FormInput<FormData> name={'bcc'} register={register} rules={valueMustBeEmail} error={errors} />

        <Label text={EmailPreviewModal.Attachments} className={styles.attachmentsPreviewLabel} />
        <div className={styles.attachmentsPreview}>
          {attachments.fields.length > 0 ? (
            attachments.fields.map((field, idx) => (
              <Fragment key={field.array_id}>
                <span>{field.name}</span>
                <DeleteIcon onClick={() => setShowDeleteConfirm({ visible: true, data: idx })} />
              </Fragment>
            ))
          ) : (
            <span className={styles.attachmentsPreviewEmptyMessage}>{EmailPreviewModal.ThereAreNoAttachments}</span>
          )}
        </div>

        <iframe srcDoc={data?.email?.body} className={styles.htmlPreview} />

        <ButtonsWrapper>
          <Button text={buttons.Cancel} theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} onClick={onClose} />
          <Button text={buttons.Send} type={'submit'} theme={BUTTON_THEMES.SUCCESS} />
        </ButtonsWrapper>
      </form>
    </Modal>
  );
};
